/**
 * Lockpicking minigame — the interactive pick/rake session over a lock's STATIC
 * seeded pin puzzle (`Lock.columns`, generated at conception in sim/lock.ts).
 * Purely headless (CLAUDE.md rule 4): this holds the transient picking STATE and
 * the step functions; the LOCK window (src/ui/lockWindow.ts) renders it and
 * main.ts routes the arrow keys to these steps while the world keeps running.
 *
 * Design (owner spec):
 * - A lock has 3..9 columns, each 3..6 pins tall, each with a hidden `target`
 *   height that seats the pin. Unlock = every column seated.
 * - PICK: ←/→ move between columns, ↑ lifts the pick in the current column, ↓
 *   shimmies (a set attempt) at the current lift. At the true height a skill roll
 *   seats it (CLICK); at a wrong height there's a small chance it binds falsely
 *   (CLACK) and the column must be re-seated at its true height (or all pins
 *   reset). The pick drops to the bottom after every shimmy.
 * - RAKE: only usable when every column is short (≤ rakeMaxPinHeight) — one ↓
 *   scrubs ALL unset columns at once, each with a per-pass seat chance, so a
 *   shallow lock rakes open in a few passes.
 */

import { CONFIG } from '../config';
import type { Lock } from './lock';

export type PickTool = 'pick' | 'rake';

/** Last resolved event, for the UI's feedback line + effects.
 *  'clack' = a genuine false set; 'jammed' = re-poked an already-jammed column;
 *  'rake' = a rake pass that seated nothing this time — INCLUDING a lock the rake
 *  simply can't bite. Both look identical (the columns just shake), so the player
 *  gets no tell about whether raking this lock type can ever work. */
export type PickFeedback = 'click' | 'clack' | 'reset' | 'open' | 'jammed' | 'rake' | null;

/** Per-column runtime state during a picking session. `target` is known to the
 *  logic but hidden from the player — they feel it out. */
export interface PickColumnState {
  height: number;
  target: number;
  set: boolean;
  falseSet: boolean;
}

export interface PickSession {
  lockId: number;
  securityLevel: number;
  columns: PickColumnState[];
  /** Active pin column (the pick's position along the keyhole). */
  cursor: number;
  /** Pick position within the active column. -1 = the pick rests OUTSIDE, below
   *  the columns (its home); 0 = the bottom slot .. gridHeight-1 = the top. The
   *  player must ↑ to ENTER a column before a shimmy can set anything — so even
   *  a pin resting at the bottom feels like a deliberate pick, not a freebie. */
  lift: number;
  /** Uniform navigable height for EVERY column — the pick reaches this high in
   *  all columns regardless of how many pins each actually holds, so a short
   *  column can't be inferred from where the pick stops. It's a guessing game:
   *  neither the per-column heights nor the targets are ever shown. */
  gridHeight: number;
  tool: PickTool;
  /** True when every column is short enough for the rake to work. */
  rakeable: boolean;
  feedback: PickFeedback;
  solved: boolean;
}

/** Begin a picking session over `lock`, copying its static pin layout. */
export function startPick(lock: Lock): PickSession {
  const columns: PickColumnState[] = lock.columns.map((c) => ({
    height: c.height,
    target: c.target,
    set: false,
    falseSet: false,
  }));
  const rakeable = columns.length > 0 && columns.every((c) => c.height <= CONFIG.lock.rakeMaxPinHeight);
  // One navigable depth for all columns (the deepest column) — hides per-column
  // heights. Every target is ≤ its column height ≤ gridHeight, so all reachable.
  const gridHeight = columns.reduce((m, c) => Math.max(m, c.height), 1);
  return {
    lockId: lock.id,
    securityLevel: lock.securityLevel,
    columns,
    cursor: 0,
    lift: -1, // the pick starts OUTSIDE the columns; ↑ enters the active one
    gridHeight,
    tool: 'rake', // rake is the first tool selected (Tab swaps to the pick)
    rakeable,
    feedback: null,
    solved: false,
  };
}

/** ←/→ : move the pick to another column. The pick withdraws to its home OUTSIDE
 *  the columns; the player must ↑ to enter the new column. */
export function pickMoveCursor(s: PickSession, dir: -1 | 1): void {
  if (s.solved || s.columns.length === 0) return;
  if (s.tool === 'rake') return; // raking scrubs every column at once — no cursor to roam
  const n = s.columns.length;
  s.cursor = (s.cursor + dir + n) % n;
  s.lift = -1;
  s.feedback = null;
}

/** ↑ : lift the pick one notch higher. From its home (-1) this ENTERS the column
 *  at the bottom slot. Capped at the UNIFORM gridHeight (not the active column's
 *  own height) so every column is navigable to the same depth. */
export function pickLift(s: PickSession): void {
  if (s.solved) return;
  if (s.columns.length === 0) return;
  if (s.tool === 'rake') return; // the rake has no per-column lift — it works the whole stack
  s.lift = Math.min(s.gridHeight - 1, s.lift + 1);
  s.feedback = null;
}

/** Tab : swap tools. Rake is ALWAYS selectable — it simply won't seat tall pins
 *  (you're free to try). */
export function pickToggleTool(s: PickSession): void {
  if (s.solved) return;
  s.tool = s.tool === 'pick' ? 'rake' : 'pick';
  s.feedback = null; // a clean slate so a stale pick 'click' can't fire the rake sweep
}

/** Space : release tension — drop every pin (clears all sets + false sets). */
export function pickReset(s: PickSession): void {
  for (const c of s.columns) {
    c.set = false;
    c.falseSet = false;
  }
  s.lift = -1;
  s.solved = false;
  s.feedback = 'reset';
}

/** ↓ : shimmy. With the PICK, a single set attempt on the active column at the
 *  current lift; with the RAKE, one pass across every unset column. */
export function pickShimmy(s: PickSession, rng: () => number = Math.random): void {
  if (s.solved) return;
  if (s.tool === 'rake') rakePass(s, rng);
  else pickAttempt(s, rng);
  checkSolved(s);
}

function pickAttempt(s: PickSession, rng: () => number): void {
  const col = s.columns[s.cursor];
  if (!col) return;
  const cfg = CONFIG.lock;
  if (s.lift < 0) {
    // The pick is still resting outside the column — nothing to set until the
    // player has ↑'d into it. A shimmy from here is a no-op.
    s.feedback = null;
    return;
  }
  if (col.set) {
    s.lift = -1;
    return;
  }
  if (col.falseSet) {
    // A jammed column can't be worked again until a DIFFERENT column is cleared
    // (which releases a random jam — see releaseRandomJam). Reset to try anew.
    s.feedback = 'jammed';
    s.lift = -1;
    return;
  }
  if (s.lift === col.target) {
    // At the shear line — a skill roll decides whether it seats this shimmy.
    if (rng() < cfg.pickSkill) {
      col.set = true;
      s.feedback = 'click';
      releaseRandomJam(s, rng); // clearing a pin frees one random jammed column
    } else {
      s.feedback = null; // right spot, no bite yet — try again
    }
  } else if (rng() < cfg.falseSetChance) {
    // Wrong height — the pin binds falsely and jams this column.
    col.falseSet = true;
    s.feedback = 'clack';
  } else {
    s.feedback = null;
  }
  s.lift = -1; // the pick always drops back OUTSIDE, below the columns, after a shimmy
}

/** Seating a pin relieves tension enough to drop ONE random jammed column back
 *  to workable (unset). This is the only way to recover a false set short of a
 *  full reset — "set a different pinhole to use the jammed column again". */
function releaseRandomJam(s: PickSession, rng: () => number): void {
  const jammed: number[] = [];
  for (let i = 0; i < s.columns.length; i++) {
    const c = s.columns[i]!;
    if (c.falseSet && !c.set) jammed.push(i);
  }
  if (jammed.length === 0) return;
  const idx = jammed[Math.floor(rng() * jammed.length)]!;
  s.columns[idx]!.falseSet = false;
}

function rakePass(s: PickSession, rng: () => number): void {
  s.lift = -1;
  if (!s.rakeable) {
    // Raking is allowed, it just can't scrub tall pins — no seat. This looks
    // EXACTLY like a rakeable lock whose pass happened to bite nothing (both are
    // 'rake' → the columns just shake), so the player can't tell from feedback
    // that this lock type will never rake open.
    s.feedback = 'rake';
    return;
  }
  let any = false;
  for (const c of s.columns) {
    if (c.set) continue;
    c.falseSet = false; // the rake scrubs out jams as it goes
    if (rng() < CONFIG.lock.rakeSetChancePerPass) {
      c.set = true;
      any = true;
    }
  }
  // A seat shows green; a barren pass reads identically to an un-rakeable lock.
  s.feedback = any ? 'click' : 'rake';
}

function checkSolved(s: PickSession): void {
  if (s.columns.length > 0 && s.columns.every((c) => c.set && !c.falseSet)) {
    s.solved = true;
    s.feedback = 'open';
  }
}
